import { connect } from 'formik';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import Bugsnag from '@bugsnag/js';

import { FormikDropdownMenuSelector } from 'shared/components/DropdownMenuSelector';
import { IMenuItem } from 'shared/types/MenuItem';
import { requestService } from 'shared/utils/services/requestService';
import { transformDisplayValue } from 'shared/utils/transformDisplayValue';
import { t } from 'localization/utils/t';

const TP = 'checkout.components.AddressForm.StateDropdownField';

interface StateDropdownFieldProps {
  className?: string;
  countryIsoCode: string;
  formik?;
  label?: string;
  onMenuItemClick(params: IMenuItem): void;
  selectedState: IMenuItem;
}

export const StateDropdownField = ({
  className,
  countryIsoCode = 'US',
  onMenuItemClick,
  selectedState,
  label,
}: StateDropdownFieldProps) => {
  // CHECKOUT TODO: type states
  const [options, setOptions] = useState([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    setOptions([]);

    if (!countryIsoCode) {
      setIsLoading(false);
      return;
    }

    let isFetchingStates = true;
    setIsLoading(true);

    const getStates = async () => {
      try {
        const { states } = await requestService({
          url: `/web-api/v1/countries/${countryIsoCode}/states`,
          method: 'GET',
        });

        const transformedStates = transformDisplayValue(states, {
          display: 'name',
          value: 'stateCode',
        });

        if (isFetchingStates) {
          setOptions(transformedStates);
          setIsLoading(false);
        }
      } catch (error) {
        Bugsnag.notify(error, (event) => {
          event.severity = 'error';
          event.context = 'checkout';
        });
      }
    };

    void getStates();

    // eslint-disable-next-line consistent-return
    return () => {
      isFetchingStates = false;
    };
  }, [countryIsoCode]);

  if (!isLoading && options.length === 0) return null;

  return (
    <StateDropdownField.StyledSelector
      className={className}
      data-qa="checkout_shipping_state_selector"
      isLoading={isLoading}
      label={label || t(`${TP}.stateDropdownLabel`, 'State / Province')}
      showLabel={false}
      menuItems={options}
      name="state"
      displayValue={
        selectedState?.display || label || t(`${TP}.stateDropdownLabel`, 'State / Province')
      }
      onMenuItemClick={onMenuItemClick}
      selection={selectedState}
      autoFocus={false}
    />
  );
};

StateDropdownField.StyledSelector = styled(FormikDropdownMenuSelector)`
  margin-top: 0;
  padding-bottom: 26px;
`;

export default connect(StateDropdownField);
